import { lazy } from 'react';

import { Navigate, useRoutes } from 'react-router-dom';

import useAppRoutes from './routes';

const AccessDeniedView = lazy(() => import('@/views/error/AccessDeniedView'));
const DefaultErrorView = lazy(() => import('@/views/error/DefaultErrorView'));
const NoCameraAccessView = lazy(() => import('@/views/error/NoCameraAccessView'));
const RouteNotFoundView = lazy(() => import('@/views/error/RouteNotFoundView'));
const SiteNotFoundView = lazy(() => import('@/views/error/SiteNotFoundView'));
const LogoutPage = lazy(() => import('@/views/auth/LogoutPage'));
const VerifyCodeViewMS = lazy(() => import('@/views/sites/VerifyCodeView'));
const VersionView = lazy(() => import('@/views/misc/VersionView'));

const AddSelfies = lazy(() => import('@/views/sites/selfies/AddSelfies'));
const ManageSelfies = lazy(() => import('@/views/sites/selfies/ManageSelfies'));

const ChangeEmailView = lazy(() => import('@/views/sites/profile/ChangeEmailView'));
const ChangeNameView = lazy(() => import('@/views/sites/profile/ChangeNameView'));
const ProfileView = lazy(() => import('@/views/sites/profile/ProfileView'));
const ChangePhoneNumberView = lazy(() => import('@/views/sites/profile/ChangePhoneNumberView'));

const CampaignsView = lazy(() => import('@/views/sites/campaign/CampaignsView'));
const CampaignView = lazy(() => import('../views/sites/campaign/CampaignView'));
const CampaignEnrollmentConfirmationView = lazy(
  () => import('@/views/sites/campaign/confirmation/CampaignEnrollmentConfirmationView')
);
const CampaignStepsView = lazy(() => import('../views/sites/campaign/CampaignStepsView'));
const IdVerification = lazy(() => import('@/views/sites/campaign/id-verification/IdVerification'));
const AddCampaignPaymentMethod = lazy(
  () => import('@/views/sites/campaign/AddCampaignPaymentMethod')
);
const AccountLinked = lazy(() => import('@/views/sites/campaign/ticketing/AccountLinked'));
const LinkAccount = lazy(() => import('@/views/sites/campaign/ticketing/LinkAccount'));
// const Lava = lazy(() => import('@/views/sites/campaign/point-of-sale/Lava/Lava'));

const LandingView = lazy(() => import('@/views/auth/LandingView'));
const CheckEmailView = lazy(() => import('@/views/auth/CheckEmailView'));
const EmailView = lazy(() => import('@/views/auth/EmailView'));
const VerifyEmailView = lazy(() => import('@/views/auth/VerifyEmailView'));
const VerifyOAuthCodeView = lazy(() => import('@/views/auth/VerifyOAuthCodeView'));

const SuccessView = lazy(() => import('@/views/misc/SuccessView'));
const SupportView = lazy(() => import('@/views/misc/SupportView'));

const ManagePaymentView = lazy(() => import('@/views/sites/payment/ManagePaymentView'));
const PaymentCardAddView = lazy(() => import('@/views/sites/payment/PaymentCardAddView'));

const AuthLayout = lazy(() => import('../layouts/AuthLayout'));
const AuthRoutesLayout = lazy(() => import('../layouts/AuthRoutesLayout'));
const SitesLayout = lazy(() => import('../layouts/SitesLayout'));
const ErrorViewLayout = lazy(() => import('@/layouts/ErrorViewLayout'));

const AxsView = lazy(() => import('@/views/auth/AxsView'));
const PaciolanView = lazy(() => import('@/views/auth/PaciolanView'));
const WicketAuthView = lazy(() => import('@/views/auth/WicketAuthView'));

const useMainRoutes = () => {
  const routes = useAppRoutes();
  const mainRoutes = {
    path: '/',
    children: [
      {
        index: true,
        element: <LandingView />
      },
      {
        path: 'support',
        element: <SupportView />
      },
      {
        path: 'other',
        children: [
          {
            index: true,
            element: <Navigate to={routes.otherSuccess()} />
          },
          {
            path: 'success',
            element: <SuccessView />
          }
        ]
      },
      {
        path: 'version',
        element: <VersionView />
      },
      {
        path: 'error',
        element: <ErrorViewLayout />,
        children: [
          {
            index: true,
            element: <DefaultErrorView />
          },
          {
            path: 'no-camera-access',
            element: <NoCameraAccessView />
          },
          {
            path: 'access-denied',
            element: <AccessDeniedView />
          },
          {
            path: 'site-404',
            element: <SiteNotFoundView />
          }
        ]
      },
      {
        path: ':pathSegment',
        element: <SitesLayout />,
        children: [
          {
            element: <AuthRoutesLayout />,
            children: [
              {
                path: ':campPath/paciolan',
                element: <PaciolanView />
              },
              {
                path: 'axs',
                element: <AxsView />
              },
              {
                path: 'wicket-auth',
                element: <WicketAuthView />
              },
              {
                path: 'email',
                element: <EmailView />
              },
              {
                path: 'email/check',
                element: <CheckEmailView />
              },
              {
                path: 'email/verify/:verificationCode',
                element: <VerifyEmailView />
              },
              {
                path: 'code/verify',
                element: <VerifyCodeViewMS />
              },
              {
                path: 'oauth/verify',
                element: <VerifyOAuthCodeView />
              }
            ]
          },
          {
            element: <AuthLayout />,
            children: [
              {
                index: true,
                element: <CampaignsView />
              },
              {
                path: 'campaigns',
                element: <CampaignsView />
              },
              {
                path: ':campPath',
                children: [
                  {
                    index: true,
                    element: <CampaignView />
                  },
                  {
                    path: 'enrollment-confirmation',
                    element: <CampaignEnrollmentConfirmationView />
                  },
                  {
                    path: 'payment',
                    element: <AddCampaignPaymentMethod />,
                    children: [
                      {
                        index: true,
                        element: <ManagePaymentView />
                      },
                      {
                        path: 'manage',
                        element: <ManagePaymentView />
                      },
                      {
                        path: 'add',
                        element: <PaymentCardAddView />
                      }
                    ]
                  },
                  // {
                  //   path: 'lava',
                  //   element: <Lava />
                  // },
                  {
                    path: 'steps',
                    element: <CampaignStepsView />
                  },
                  {
                    path: 'id',
                    element: <IdVerification />
                  },
                  {
                    path: 'link-account',
                    element: <LinkAccount />
                  },
                  {
                    path: 'account-linked',
                    element: <AccountLinked />
                  },
                  {
                    path: 'selfie',
                    element: <AddSelfies onSuccessRedirectUrl="campaign" />
                  }
                ]
              },
              {
                path: 'selfie',
                element: <AddSelfies onSuccessRedirectUrl="campaigns" />
              },
              {
                path: 'profile',
                children: [
                  {
                    index: true,
                    element: <ProfileView />
                  },
                  {
                    path: 'email/change',
                    element: <ChangeEmailView />
                  },
                  {
                    path: 'phone/change',
                    element: <ChangePhoneNumberView />
                  },
                  {
                    path: 'phone/code/verify',
                    element: <VerifyCodeViewMS />
                  },
                  {
                    path: 'name/change',
                    element: <ChangeNameView />
                  },
                  {
                    path: 'selfie',
                    children: [
                      {
                        index: true,
                        element: <Navigate to={routes.selfieManage()} />
                      },
                      {
                        path: 'manage',
                        element: <ManageSelfies />
                      },
                      {
                        path: 'add',
                        element: <AddSelfies onSuccessRedirectUrl="manage" />
                      }
                    ]
                  },
                  {
                    path: 'payment',
                    children: [
                      {
                        index: true,
                        element: <Navigate to={routes.paymentManage()} />
                      },
                      {
                        path: 'manage',
                        element: <ManagePaymentView />
                      },
                      {
                        path: 'add',
                        element: <PaymentCardAddView />
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: '/logout',
        element: <LogoutPage />
      },
      {
        path: '*',
        element: <RouteNotFoundView />
      }
    ]
  };
  return useRoutes([mainRoutes]);
};

export default useMainRoutes;
